import React from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { ProjectMember, MetadataScope, DownloadScope } from "../types/api";

// Re-exported so existing importers (e.g. ImportWizard) keep working; the
// canonical definitions now live in types/api.ts.
export type { MetadataScope, DownloadScope };

export const getMetadataOptions = (
  t: TFunction,
): {
  value: MetadataScope;
  label: string;
  description: string;
}[] => [
  {
    value: "projects",
    label: t("metadataOptions.projects.label"),
    description: t("metadataOptions.projects.description"),
  },
  {
    value: "org",
    label: t("metadataOptions.org.label"),
    description: t("metadataOptions.org.description"),
  },
  {
    value: "private",
    label: t("metadataOptions.private.label"),
    description: t("metadataOptions.private.description"),
  },
];

export const getDownloadOptions = (
  t: TFunction,
): {
  value: DownloadScope;
  label: string;
  description: string;
}[] => [
  {
    value: "none",
    label: t("downloadOptions.none.label"),
    description: t("downloadOptions.none.description"),
  },
  {
    value: "project",
    label: t("downloadOptions.project.label"),
    description: t("downloadOptions.project.description"),
  },
  {
    value: "selected_members",
    label: t("downloadOptions.selectedMembers.label"),
    description: t("downloadOptions.selectedMembers.description"),
  },
];

interface UploadScopeSelectorProps {
  /** Used to namespace radio button groups — must be unique on the page */
  uniqueId: string | number;
  metadataScope: MetadataScope;
  downloadScope: DownloadScope;
  downloadUserIds: number[];
  allProjectMembers: ProjectMember[];
  /** IDs that are always checked and cannot be unchecked (e.g. the uploader) */
  lockedDownloadUserIds?: number[];
  onMetadataScopeChange: (scope: MetadataScope) => void;
  onDownloadScopeChange: (scope: DownloadScope) => void;
  onDownloadUserIdsChange: (ids: number[]) => void;
}

const sectionStyle: React.CSSProperties = {
  marginBottom: "14px",
  padding: "14px 16px",
  backgroundColor: "var(--surface-2)",
  borderRadius: "6px",
  border: "0.5px solid var(--border)",
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
  marginBottom: "10px",
  display: "block",
};

const radioOptionStyle = (selected: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "10px",
  padding: "9px 12px",
  borderRadius: "6px",
  border: `1px solid ${selected ? "var(--border-accent)" : "var(--border)"}`,
  backgroundColor: selected ? "var(--bg-accent-muted)" : "var(--surface-2)",
  cursor: "pointer",
});

/**
 * Hover/focus help bubble for the two scope headings.
 *
 * Catalog visibility and download access are orthogonal — a dataset can be
 * visible but not downloadable, or hidden but grantable by request — and that
 * is the single most confusing thing on the upload screen. Spell it out on
 * hover rather than expecting people to infer it from the radio labels.
 */
const HelpTip: React.FC<{ text: string }> = ({ text }) => (
  <span className="scope-help" tabIndex={0} role="note" aria-label={text}>
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" />
    </svg>
    <span className="scope-help-bubble">{text}</span>
  </span>
);

export const UploadScopeSelector: React.FC<UploadScopeSelectorProps> = ({
  uniqueId,
  metadataScope,
  downloadScope,
  downloadUserIds,
  allProjectMembers,
  lockedDownloadUserIds = [],
  onMetadataScopeChange,
  onDownloadScopeChange,
  onDownloadUserIdsChange,
}) => {
  const { t } = useTranslation("uploadScopeSelector");
  const metadataOptions = getMetadataOptions(t);
  const downloadOptions = getDownloadOptions(t);

  const toggleDownloadUser = (id: number) => {
    if (lockedDownloadUserIds.includes(id)) return;
    onDownloadUserIdsChange(
      downloadUserIds.includes(id)
        ? downloadUserIds.filter((x) => x !== id)
        : [...downloadUserIds, id],
    );
  };

  return (
    <>
      <style>{`
        .scope-help {
          position: relative;
          display: inline-flex;
          align-items: center;
          margin-left: 5px;
          color: var(--text-muted);
          cursor: help;
          vertical-align: middle;
        }
        .scope-help:hover, .scope-help:focus-visible {
          color: var(--text-accent);
          outline: none;
        }
        .scope-help-bubble {
          position: absolute;
          bottom: calc(100% + 7px);
          left: 50%;
          transform: translateX(-50%);
          width: 260px;
          padding: 9px 11px;
          border-radius: var(--radius);
          background: var(--text-primary);
          color: #fff;
          font-size: 12px;
          font-weight: 400;
          line-height: 1.45;
          text-transform: none;
          letter-spacing: normal;
          box-shadow: 0 4px 14px rgba(0,0,0,0.18);
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.12s;
          z-index: 60;
          pointer-events: none;
        }
        .scope-help:hover .scope-help-bubble,
        .scope-help:focus-visible .scope-help-bubble {
          opacity: 1;
          visibility: visible;
        }
        .scope-help-bubble::after {
          content: "";
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: var(--text-primary);
        }
      `}</style>

      {/* Catalog visibility */}
      <div style={sectionStyle}>
        <span style={sectionLabelStyle}>
          {t("catalogVisibility.heading")}
          <HelpTip text={t("catalogVisibility.helpTip")} />
        </span>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {metadataOptions.map((opt) => (
            <label
              key={opt.value}
              style={radioOptionStyle(metadataScope === opt.value)}
            >
              <input
                type="radio"
                name={`metadata-scope-${uniqueId}`}
                value={opt.value}
                checked={metadataScope === opt.value}
                onChange={() => onMetadataScopeChange(opt.value)}
                style={{ marginTop: "2px", accentColor: "var(--fill-primary)" }}
              />
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "var(--text-primary)",
                  }}
                >
                  {opt.label}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-muted)",
                    marginTop: "2px",
                  }}
                >
                  {opt.description}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Download access */}
      <div style={sectionStyle}>
        <span style={sectionLabelStyle}>
          {t("downloadAccess.heading")}
          <HelpTip text={t("downloadAccess.helpTip")} />
        </span>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {downloadOptions.map((opt) => (
            <label
              key={opt.value}
              style={radioOptionStyle(downloadScope === opt.value)}
            >
              <input
                type="radio"
                name={`download-scope-${uniqueId}`}
                value={opt.value}
                checked={downloadScope === opt.value}
                onChange={() => {
                  onDownloadScopeChange(opt.value);
                  if (opt.value !== "selected_members")
                    onDownloadUserIdsChange([]);
                }}
                style={{ marginTop: "2px", accentColor: "var(--fill-primary)" }}
              />
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "var(--text-primary)",
                  }}
                >
                  {opt.label}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-muted)",
                    marginTop: "2px",
                  }}
                >
                  {opt.description}
                </div>
              </div>
            </label>
          ))}
        </div>

        {downloadScope === "selected_members" &&
          allProjectMembers.length > 0 && (
            <div
              style={{
                marginTop: "10px",
                paddingTop: "10px",
                borderTop: "0.5px solid var(--border)",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  color: "var(--text-secondary)",
                  marginBottom: "6px",
                  fontWeight: 600,
                }}
              >
                {t("downloadAccess.membersWhoCanDownload")}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {allProjectMembers
                  .filter((m) => !lockedDownloadUserIds.includes(m.id))
                  .map((m) => {
                    const checked = downloadUserIds.includes(m.id);
                    return (
                      <label
                        key={m.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: "12px",
                          padding: "4px 10px",
                          backgroundColor: checked
                            ? "var(--bg-accent-muted)"
                            : "var(--surface-2)",
                          borderRadius: "4px",
                          border: `1px solid ${checked ? "var(--border-accent)" : "var(--border)"}`,
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleDownloadUser(m.id)}
                          style={{ marginRight: "5px" }}
                        />
                        {m.name}
                      </label>
                    );
                  })}
              </div>
              {downloadUserIds.filter(
                (id) => !lockedDownloadUserIds.includes(id),
              ).length === 0 && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-muted)",
                    marginTop: "6px",
                  }}
                >
                  {t("downloadAccess.onlyYouCanDownload")}
                </div>
              )}
            </div>
          )}
      </div>
    </>
  );
};
